import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import Avatar from '../shared/Avatar';
import { BarChartIcon, PlusIcon, XIcon, ZapIcon } from '../shared/Icons';

const AUTO_SOURCES = [
  { value: 'manual', label: 'Manual entry' },
  { value: 'auto_revenue', label: 'Auto: Revenue' },
  { value: 'auto_demand', label: 'Auto: Demand (HVAC Service P1)' },
  { value: 'auto_recall', label: 'Auto: Recalls' },
  { value: 'auto_warranty', label: 'Auto: Warranty' },
  { value: 'auto_duct_cleaning', label: 'Auto: Duct Cleaning' },
  { value: 'auto_duct_cleaning_revenue', label: 'Auto: Duct Cleaning Revenue' },
  { value: 'auto_net_new_adds', label: 'Auto: Membership Net New Adds' },
  { value: 'auto_tech_adds', label: 'Auto: Membership Tech Adds' },
  { value: 'auto_sales_adds', label: 'Auto: Membership Sales Adds' },
  { value: 'auto_plumbing_adds', label: 'Auto: Membership Plumbing Adds' },
  { value: 'auto_call_centre_adds', label: 'Auto: Membership Call Centre Adds' },
  { value: 'auto_leads_marketed', label: 'Auto: Marketed Leads' },
  { value: 'auto_leads_tech', label: 'Auto: Tech Leads' },
];

function fmtVal(v, unit) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (unit === 'currency') return (Math.abs(n) >= 1000 ? '$' + (n / 1000).toFixed(1) + 'k' : '$' + n.toFixed(0));
  if (unit === 'percent') return n.toFixed(1) + '%';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function goalMet(metric, value) {
  if (value == null || metric.goal_value == null) return null;
  return metric.goal_operator === '<=' ? value <= metric.goal_value : value >= metric.goal_value;
}

function EditCell({ value, unit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const start = () => { setVal(value ?? ''); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const n = Number(val);
    if (val !== '' && !isNaN(n) && n !== Number(value ?? NaN)) onSave(n);
  };
  if (editing) {
    return <input autoFocus type="number" value={val} onChange={e => setVal(e.target.value)}
      onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ width: '55px', padding: '0.1rem 0.25rem', border: `1.5px solid ${colors.primary}`, borderRadius: '0.2rem', fontSize: '0.7rem', textAlign: 'right' }}
    />;
  }
  return (
    <div onClick={start} style={{ cursor: 'pointer', minWidth: '45px', textAlign: 'right' }} title="Click to edit">
      {fmtVal(value, unit)}
    </div>
  );
}

function AddMetricForm({ team, onAdd, onCancel }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('number');
  const [source, setSource] = useState('manual');
  const [goalOperator, setGoalOperator] = useState('>=');
  const [goalValue, setGoalValue] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onAdd({
        name: name.trim(), unit, source, goal_operator: goalOperator,
        goal_value: goalValue === '' ? null : Number(goalValue),
        owner_user_id: ownerUserId || null,
      });
      onCancel();
    } catch {} finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.85rem 1rem', background: colors.bgSubtle, borderRadius: '0.75rem', border: `1.5px solid ${colors.border}`, flexWrap: 'wrap', marginBottom: '0.9rem' }}>
      <select value={ownerUserId} onChange={e => setOwnerUserId(e.target.value)} style={{ ...st.input, width: '150px' }}>
        <option value="">No owner</option>
        {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Metric name" style={st.input} autoFocus />
      <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...st.input, width: '100px' }}>
        <option value="number">Number</option>
        <option value="currency">Currency</option>
        <option value="percent">Percent</option>
      </select>
      <select value={goalOperator} onChange={e => setGoalOperator(e.target.value)} style={{ ...st.input, width: '60px' }}>
        <option value=">=">&#8805;</option>
        <option value="<=">&#8804;</option>
      </select>
      <input value={goalValue} onChange={e => setGoalValue(e.target.value)} type="number" placeholder="Goal" style={{ ...st.input, width: '90px' }} />
      <select value={source} onChange={e => setSource(e.target.value)} style={{ ...st.input, width: '220px' }}>
        {AUTO_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <button type="submit" disabled={saving} style={st.primaryBtn}>{saving ? '...' : 'Add'}</button>
      <button type="button" onClick={onCancel} style={st.secondaryBtn}>Cancel</button>
    </form>
  );
}

function formatWeekHeader(weekStart) {
  const d = new Date(weekStart + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export default function ScorecardSection() {
  const [metrics, setMetrics] = useState([]);
  const [weekStarts, setWeekStarts] = useState([]);
  const [values, setValues] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [metricsRes, valuesRes, teamRes] = await Promise.all([
        apiClient.get('/meetings/scorecard/metrics'),
        apiClient.get('/meetings/scorecard/values?weeks=13'),
        apiClient.get('/meetings/team'),
      ]);
      setMetrics(metricsRes.data.metrics || []);
      setWeekStarts(valuesRes.data.weekStarts || []);
      setValues(valuesRes.data.values || []);
      setTeam(teamRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const valueMap = useMemo(() => {
    const m = {};
    for (const v of values) m[`${v.metric_id}_${v.week_start}`] = v;
    return m;
  }, [values]);

  const saveValue = async (metricId, weekStart, value) => {
    await apiClient.put(`/meetings/scorecard/values/${metricId}/${weekStart}`, { value });
    await load();
  };

  const addMetric = async (data) => {
    await apiClient.post('/meetings/scorecard/metrics', data);
    await load();
  };

  const deleteMetric = async (id) => {
    if (!window.confirm('Delete this metric?')) return;
    await apiClient.delete(`/meetings/scorecard/metrics/${id}`);
    await load();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><BarChartIcon size={18} /> Metrics</h2>
        <button onClick={() => setShowAdd(s => !s)} style={{ ...st.primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><PlusIcon size={13} /> Add Metric</button>
      </div>
      {showAdd && <AddMetricForm team={team} onAdd={addMetric} onCancel={() => setShowAdd(false)} />}
      {metrics.length === 0 ? (
        <div style={st.emptyState}><BarChartIcon size={28} style={{ color: colors.faint }} />No metrics yet. Add one to get started.</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '0.6rem', border: `1px solid ${colors.border}` }}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={{ ...st.th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, minWidth: '52px', borderTopLeftRadius: '0.6rem' }}></th>
                <th style={{ ...st.th, textAlign: 'left', position: 'sticky', left: '52px', zIndex: 2, minWidth: '190px' }}>Metric</th>
                <th style={st.th}>Goal</th>
                {weekStarts.map(w => <th key={w} style={st.th}>{formatWeekHeader(w)}</th>)}
                <th style={{ ...st.th, width: '24px' }}></th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => {
                const rowBg = idx % 2 !== 0 ? colors.bgSubtle : '#fff';
                const isAuto = metric.source && metric.source !== 'manual';
                return (
                  <tr key={metric.id} style={{ background: rowBg }}>
                    <td style={{ ...st.td, textAlign: 'left', position: 'sticky', left: 0, background: rowBg }}>
                      <Avatar name={metric.owner_name} size={20} />
                    </td>
                    <td style={{ ...st.td, textAlign: 'left', position: 'sticky', left: '52px', background: rowBg, fontWeight: '700' }}>
                      {metric.name}
                      {isAuto && <ZapIcon size={11} style={{ color: colors.amber, marginLeft: '0.3rem' }} title="Auto-linked" />}
                    </td>
                    <td style={{ ...st.td, fontSize: '0.65rem', color: colors.faint }}>
                      {metric.goal_value != null ? `${metric.goal_operator} ${fmtVal(metric.goal_value, metric.unit)}` : '—'}
                    </td>
                    {weekStarts.map(w => {
                      const cell = valueMap[`${metric.id}_${w}`];
                      const value = cell?.value ?? null;
                      const met = goalMet(metric, value);
                      const cellStyle = {
                        background: met === true ? colors.greenSoft : met === false ? colors.redSoft : undefined,
                        color: met === true ? colors.green : met === false ? colors.red : colors.text,
                        fontWeight: '700',
                      };
                      return (
                        <td key={w} style={{ ...st.td, ...cellStyle }}>
                          <EditCell value={value} unit={metric.unit} onSave={v => saveValue(metric.id, w, v)} />
                        </td>
                      );
                    })}
                    <td style={{ ...st.td, textAlign: 'center' }}>
                      <button onClick={() => deleteMetric(metric.id)} style={st.ghostBtn} title="Delete"><XIcon size={12} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
